/**
 * Subscription Detection Helper Module
 * Provides utility functions to detect subscription items in cart
 */

/**
 * Check if cart contains subscription items
 * @param {Object} cart - The cart object
 * @returns {boolean} - True if cart has subscription items
 */
export function hasSubscriptionInCart(cart) {
  if (!cart || !cart.items) return false

  return cart.items.some(item => {
    // Check if item has selling plan allocation (indicates subscription)
    return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
  })
}

/**
 * Get product-specific gift metafield from products in cart
 * @param {Object} cart - The cart object
 * @returns {Object|null} - Gift variant info or null if no specific gift found
 */
export function getProductSpecificGift(cart) {
  if (!cart || !cart.items) return null

  // Find first product with subscription that has a gift metafield
  for (const item of cart.items) {
    // Check if it's a subscription item with gift data
    if (item.selling_plan_allocation && item.properties?._first_subscription_gift_variant_id) {
      const giftMapping = {
        variantId: item.properties._first_subscription_gift_variant_id,
        productId: item.properties._first_subscription_gift_product_id,
        sourceProductId: item.product_id,
        sourceProductTitle: item.product_title
      }

      return giftMapping
    }
  }

  return null
}

/**
 * Get subscription items from cart
 * @param {Object} cart - The cart object
 * @returns {Array} - Array of subscription items
 */
export function getSubscriptionItems(cart) {
  if (!cart || !cart.items) return []

  return cart.items.filter(item => {
    return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
  })
}

/**
 * Check if a specific gift is already in cart
 * @param {Object} cart - The cart object
 * @param {string} giftType - Type of gift to check ('subscription' or 'product-specific')
 * @returns {boolean} - True if gift is already in cart
 */
export function hasSubscriptionGiftInCart(cart, giftType = 'subscription') {
  if (!cart || !cart.items) return false

  return cart.items.some(item => {
    if (giftType === 'subscription') {
      return item.properties?._isSubscriberGift === true
    } else if (giftType === 'product-specific') {
      return item.properties?._isProductSpecificGift === true
    }

    // Check for either type
    return item.properties?._isSubscriberGift === true ||
           item.properties?._isProductSpecificGift === true
  })
}

/**
 * Remove subscription gifts if no longer eligible
 * @param {Object} cart - The cart object
 * @returns {Array} - Array of gift items that should be removed
 */
export function getSubscriptionGiftsToRemove(cart) {
  if (!cart || !cart.items) return []

  const hasSubscription = hasSubscriptionInCart(cart)

  if (!hasSubscription) {
    // No subscription in cart, remove all subscription gifts
    return cart.items.filter(item => {
      return item.properties?._isSubscriberGift === true ||
             item.properties?._isProductSpecificGift === true
    })
  }

  return []
}
